/*
 * Copyright (c) 2021.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {HttpParams} from '@angular/common/http';
import {ChangeDetectionStrategy, Component, ElementRef, HostListener, inject, Input, OnInit, ViewChild} from '@angular/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {NbAutocompleteDirective, NbButtonModule, NbIconModule, NbInputModule} from '@nebular/theme';
import {BaseComponent, CustomerGroup, FileService, ProductCustomer, StoreService} from 'compob2b-libs';
import {Observable, of, Subject} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, map, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {SearchCodeHighlightComponent} from '../search-code-highlight/search-code-highlight.component';
import {literalLength} from '../search-code-highlight/code-highlight.mjs';
import {ModalProductComponent} from '../product/modal-product/modal-product.component';
import {ProductService} from '../product/services/product.service';
import {UtilsService} from '../utils/utils.service';
import {
  ProductAvailabilityComponent
} from '../product/product-list/product-availability/product-availability.component';
import {ProductPriceComponent} from '../product/product-list/product-price/product-price.component';
import {AddToCartComponent} from '../cart/add-to-cart/add-to-cart.component';
import {NgForOf, NgIf} from '@angular/common';
import { DeviceService } from '../../../../../compob2b-libs/src/lib/services/device.service';
import {
  AddToRequestedProductsPopupComponent
} from "../product/add-to-requested-products-popup/add-to-requested-products-popup.component";

@Component({
    selector: 'app-search-autocomplete',
    templateUrl: './search-autocomplete.component.html',
    styleUrl: './search-autocomplete.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SearchCodeHighlightComponent,
    ProductAvailabilityComponent,
    ProductPriceComponent,
    AddToCartComponent,
    RouterLink,
    NbInputModule,
    ReactiveFormsModule,
    NbIconModule,
    NgIf,
    NgForOf,
    NbButtonModule
  ]
})

export class SearchAutocompleteComponent extends BaseComponent implements OnInit {
  @Input() mode = 'search';
  @ViewChild(NbAutocompleteDirective) autoComplete: NbAutocompleteDirective<any>;
  @ViewChild('autoCompleteInput') autoCompleteInput: ElementRef<HTMLElement>;
  readonly customerGroup$ = this.storeService.getCustomerGroup();

  productService = inject(ProductService);
  device =  inject(DeviceService);

  private readonly cancelSearch = new Subject<void>();
  inputFormControl: FormControl;
  visible = false;
  terms: ProductCustomer[] = [];
  isShowcase: boolean = false;

  constructor(
    private readonly storeService: StoreService,
    protected readonly route: ActivatedRoute,
    private readonly fileService: FileService,
    private readonly elementRef: ElementRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.isShowcase = UtilsService.isCurrentDomainShowcase();
    this.inputFormControl = new FormControl();
    this.inputFormControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        tap(() => this.cancelSearch.next()),
        debounceTime(300),
        switchMap(term => this.searchTerms(term)),
        takeUntil(this.unsubscribe)
      ).subscribe(terms => {
        this.terms = terms;
        if (terms.length && this.visible) this.show();
        else this.hide();
      });

    this.route.queryParamMap
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(param => {
        this.inputFormControl.patchValue(param.get('keyword'));
        this.cdr.detectChanges();
      });

    this.customerGroup$
      .pipe(
        tap(data => this.handleCustomerGroup(data)),
        takeUntil(this.unsubscribe)
      ).subscribe();
  }

  show() {
    this.visible = true;
    this.cdr.detectChanges();
  }

  hide() {
    this.cancelSearch.next();
    this.terms = [];
    this.visible = false;
    this.cdr.detectChanges();
  }

  openModalProduct(product: ProductCustomer): void {
    if (!product) return
    const uid = product.sync === false ? product.syncUidPim : product.syncUid;
    this.hide();
    if (this.device.isMobile) {
      this.router.navigate(['/product/', uid]);
    } else {
      this.productService.openModalProduct(ModalProductComponent, uid);
    }
  }

  public getImageUri(picture: string) {
    return picture ? this.fileService.getImageSrc(picture, 800) : '/assets/images/product/nofoto.jpg';
  }

  searchTerms(term: string): Observable<ProductCustomer[]> {
    if (!term || literalLength(term) < 2 || !this.visible) {
      return of([]);
    }
    let params = new HttpParams().set('keyword', term);
    if (this.productService.customerGroup) {
      params = params.set('region', this.productService.customerGroup.id);
    }
    const endpoint = this.isRequestedProducts
      ? '/product/search-requested-products-autocomplete'
      : '/product/search-autocomplete';
    return this.compoApiService.getWithParams<ProductCustomer[]>(endpoint, params).pipe(
      take(1),
      takeUntil(this.cancelSearch),
      map(data => data.data || []),
      catchError(() => of([]))
    );
  }

  @HostListener('document:keydown.escape')
  @HostListener('window:popstate', ['$event'])
  onHide(): void {
    this.hide();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: any): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.hide();
    } else {
      this.show();
    }
  }

  @HostListener('keydown', ['$event'])
  onKeydown($event: KeyboardEvent) {
    if ($event.keyCode == 13) {
      this.onSubmit();
    } else if ($event.keyCode == 27) {
      this.hide();
    } else {
      this.show();
    }
  }

  get value() {
    return this.inputFormControl.value;
  }

  get isSpecification() {
    return this.mode == 'specification' || this.mode == 'requested-products';
  }

  get isSearch() {
    return this.mode == 'search';
  }

  get isRequestedProducts() {
    return this.mode == 'requested-products';
  }

  onSubmit() {
    if (this.isSearch) {
      this.hide();
      this.router.navigate(['/catalog'], {
        queryParams: {keyword: this.value, page: 1, catalog_id: null, templateId: null},
        queryParamsHandling: 'merge'
      });
    }
  }

  openAddToRequestedProducts(product: ProductCustomer): void {
    this.windowService.open(AddToRequestedProductsPopupComponent, {
      context: {
        product,
        amount: 1
      }
    });
  }

  isShowRequestedProducts(product: ProductCustomer) : boolean {
    return this.productService.isShowRequestedProducts(product);
  }

  private handleCustomerGroup(customerGroup: CustomerGroup) {
    this.productService.handleCustomerGroup(customerGroup);
    this.cdr.detectChanges();
  }
}
